import React from 'react';

// import default view
import { singleSelectAndSearch as SingleSelectAndSearchView } from './singleSelectAndSearch.view';
import { get, isEqual, isEmpty, find, isUndefined, toInteger, cloneDeep } from 'lodash';
import { SingleSelectAndSearchService } from 'services/singleSelectAndSearch';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { messageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { singleSelectAndSearch, SingleSelectAndSearch } from 'modules/singleSelectAndSearch';
 *
 * // create a component
 * const ComponentWithHOC = singleSelectAndSearch( CustomView );
 */
export class SingleSelectAndSearch extends React.Component {
    constructor( props ) {
        super( props );

        this.state = {
            url: get( this.props, 'url' ),
            options: [],
            selectedOption: undefined,
            pagination: get( props, 'requestParams', {} ),
            fetchingType: undefined
        };

        // create service instance
        this.singleSelectAndSearchService = new SingleSelectAndSearchService();

        // bind
        this.__bind();
       
    }

    __bind() {
        this.handleQueryChange = this.handleQueryChange.bind( this );
        this.onOptionSelect = this.onOptionSelect.bind( this );
        this.handleClearSelection = this.handleClearSelection.bind( this );
        this.handleOptionPagination = this.handleOptionPagination.bind( this );
    }
    
    /**
     * when user searches a text
     */
    async handleQueryChange( { query, paginate = false } ) {
        log.debug( 'SingleSelectAndSearch.handleQueryChange()' );

        this.singleSelectAndSearchService.cancel();
        const { status, data } = await this.singleSelectAndSearchService.fetchOptions( {

            url: this.props.url,
            data: {
                [ this.props.payloadKey ]: query,
                pagination: this.state.pagination
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            return null; // ignore on request cancellations
        } else if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );

            return [];

        } else {

            // append items based on pagination
            let _items = get( data, 'options' );
            if( paginate ) {
                _items = this.state.options.concat( _items );
            }

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                options: _items,
            } );

            return { options: _items, status: status } ;
        }
    }

    /**
     * action executed when user clicks on a option
     */
    onOptionSelect( value ) {
        log.debug( 'SingleSelectAndSearch.onOptionSelect()' );
        this.props.onChange( { options: this.state.options, value } );
    }

    /**
     * to clear the selected value
     */
    handleClearSelection() {
        log.debug( 'SingleSelectAndSearch.handleClearSelection()' );
        if( this.props.removeSelectedOptionOnClear ) {
            this.setState( { options: [], selectedOption: '' }, () => {
                this.props.onChange( { options: [], value: '' } );
            } );
        } else {
            this.props.onChange( { options: [], value: '' } );
        }
    }

    handleOptionPagination( { query } ) {
        log.debug( 'SingleSelectAndSearch.handleOptionPagination()' );

        // increment start index
        const nextStartIndex = toInteger( get( this.state, 'pagination.startIndex', 0 ) ) + toInteger( get( this.state, 'pagination.chunkSize', 0 ) );
        
        // update pagination start index (startIndex)
        this.setState( {

            // isFetching: true,
            // fetchingType: 'pagination',

            pagination: {
                ...this.state.pagination,
                startIndex: nextStartIndex, // update start index
            }
        }, async () => {

            this.singleSelectAndSearchService.cancel();

            const { status, data } = await this.singleSelectAndSearchService.fetchOptions( {

                // url: paginate ? paginationUrl : this.props.url,
                url: this.props.url,
                data: {
                    [ this.props.payloadKey ]: query,
                    pagination: this.state.pagination
                }
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );

                return [];

            } else {

                // append items based on pagination
                const _items = cloneDeep( get( data, 'options' ) );
                const newList = [ ...this.state.options, ..._items ];

                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    options: newList,
                }, () => {

                    messageBus.trigger( `${MESSAGE_BUS_EVENTS.CHOIOCE_LIST_UPDATED}_${this.props.name}`, { options: get( data, 'options' ) } );

                 } );
            }
        } );
    }

    UNSAFE_componentWillReceiveProps( nextProps ) {
        log.debug( 'SingleSelectAndSearch.componentWillReceiveProps()' );
        
        if( ! isEqual( nextProps.value, get( this.state, 'selectedOption.value' ) ) ) {

            if( isEmpty( nextProps.value ) ) {
               this.setState( { selectedOption: undefined } );
            } else {
            
                const selected = find( get( this.state, 'options', [] ), { value: nextProps.value } );
                this.setState( {
                    selectedOption: ! isUndefined( selected ) ? selected: { title: { value: nextProps.value } }
                } );
            }
        }
    }
    
    componentWillUnmount() {
        this.singleSelectAndSearchService.cancel(); // cancel ongoing requests
    }

    render() {
        return (
            <SingleSelectAndSearchView
                { ...this.props }
                { ...this.state }
                handleQueryChange={ this.handleQueryChange }
                onChange={ this.onOptionSelect }
                clearSelection={ this.handleClearSelection }
                onPaginationCallBack={ this.handleOptionPagination }
            />
        );
    }
}

 // set display name
 SingleSelectAndSearch.displayName = 'SingleSelectAndSearch';
    
 // default props
 SingleSelectAndSearch.defaultProps = {
    isLabelVisible: true, // if want to show Top Label
    showDefaultLabelSelect: false, // show select label instead of ALL
    classForBottomTitle: undefined, // change styling of the Select or ALL ( when nothing else is selected)
    classForBottomValue: undefined, // change styling of Selected option's Value
    removeSelectedOptionOnClear: true, // remove selected option when clear the selection
    requestParams: {}, //additional request params
    disablePagination: true
 };

 // prop types
 SingleSelectAndSearch.propTypes = {};
